import { Box, Button, Checkbox, Chip, ChipProps, CircularProgress, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { memo, useEffect, useState } from "react";
import { Classroom, Enrollment, Payout, PayoutStatus } from "../../models/classroom";
import { CheckCircle, ChevronLeft, ChevronRight, CloudDownload, Download, Error, PaymentSharp, Refresh, Schedule } from "@mui/icons-material";
import { useClassroomActions } from "../../hooks/useClassroomActions";
import PaginationTracker from "../../types/pagination-tracker";

interface PayoutsTableProps {
    selectedClassroom: Classroom;
    fetchPayouts: () => Promise<void>;
    tracker: PaginationTracker;
}

function PayoutsTable({ selectedClassroom, fetchPayouts, tracker }: PayoutsTableProps) {

    const { getPayoutData } = useClassroomActions();

    const [refreshingId, setRefreshingId] = useState(0);

    const [payouts, setPayouts] = useState<Payout[]>([]);

    const [loading, setLoading] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 50;
    
    const totalLength = payouts.length;

    const totalPages = Math.ceil(totalLength / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;

    const paginatedPayouts = payouts.slice(startIndex, startIndex + itemsPerPage);
    
    
    const handlePreviousPage = () => {
        setCurrentPage((prev) => Math.max(prev - 1, 1));
    };
    
    const handleNextPage = () => {
        setCurrentPage((prev) => Math.min(prev + 1, totalPages));
    };


    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        });
    };

    
    const handleDownloadCSV = () => {
        const headers = ['Amount', 'Status', 'Reference', 'Time'];
        const csvContent = [
            headers.join(','),
            ...(selectedClassroom?.payouts || []).map(payout => [
            `"${payout.amount}"`,
            `"${payout.status === PayoutStatus.SUCCESSFUL ? 'successful' : payout.status === PayoutStatus.PENDING ? 'pending' : 'error'}"`,
            `"${payout.reference}"`,
            `"${formatDate(payout.time)}"`,
            ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `elms_${selectedClassroom?.name || 'classroom'}_payouts.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const refreshPayout = async (payout_id: number) => {
        setRefreshingId(payout_id);
        await getPayoutData(selectedClassroom.id, payout_id);
        setRefreshingId(0)
    };

    const loadMorePayouts = async () => {
        if (loading) return;
        
        setLoading(true);
        await fetchPayouts();
        setCurrentPage(totalPages + 1);
        setLoading(false);
    }

    useEffect(() => {
        setPayouts(selectedClassroom.payouts!);
    }, [selectedClassroom.payouts]);
    
    const hasMore = selectedClassroom.id in tracker && tracker[selectedClassroom.id].canLoadMore;

    
    return (
        <Paper
            elevation={1}
            sx={{ 
                borderRadius: 3, 
                border: '1px solid',
                borderColor: 'grey.300',
                overflow: 'hidden'
            }}
        >
            <TableContainer>  
                <Table>
                    <TableHead>
                        <TableRow sx={{  }}>
                            <TableCell sx={{ fontWeight: 600, color: "grey.700" }}>
                                Amount
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: "grey.700" }}>
                                Status
                            </TableCell>
                             <TableCell sx={{ fontWeight: 600, color: "grey.700" }}>
                                Reference
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: "grey.700" }}>
                                Time
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: "grey.700" }}>
                                
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {paginatedPayouts.map((payout) => {
                            return (
                                <TableRow 
                                    key={`${payout.id}`}
                                    sx={{
                                        "&:hover": { 
                                            bgcolor: "grey.50"
                                        },
                                        bgcolor: 'inherit'
                                    }}
                                >
                                    <TableCell>
                                        <Typography 
                                            variant="body2" 
                                            fontWeight={500}
                                            sx={{ color: 'grey.900', mb: 0.5 }}
                                        >
                                            ₦{payout.amount}
                                        </Typography>
                                    </TableCell>

                                    <TableCell>
                                        <Typography variant="body2" component="span" sx={{ color: 'grey.800' }}>
                                            {payout.status === PayoutStatus.SUCCESSFUL ? (
                                                <Chip
                                                    label="Successful"
                                                    color="success"
                                                    icon={<CheckCircle />}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            ) : payout.status === PayoutStatus.PENDING ? (
                                                <Chip
                                                    label="Pending"
                                                    color="warning"
                                                    icon={<Schedule />}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            ) : (
                                                <Chip
                                                    label="Error"
                                                    color="error"
                                                    icon={<Error />}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            )}
                                        </Typography>
                                    </TableCell>
                                    
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: 'grey.800' }}>
                                            {payout.reference}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: 'grey.800' }}>
                                            {formatDate(payout.time)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {payout.status === PayoutStatus.PENDING && (
                                            refreshingId === payout.id ? (
                                                <CircularProgress size={18} />
                                            ) : (
                                                <IconButton
                                                    color="primary"
                                                    size="small"
                                                    onClick={() => refreshPayout(payout.id)}
                                                    title="Retry payout"
                                                >
                                                    <Refresh />
                                                </IconButton>
                                            )
                                        )}

                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box 
            sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: 1,
                backgroundColor: 'grey.100',
                gap: 3
            }}
        >
        
            <Typography
                variant="body2"
                sx={{
                    cursor: !hasMore ? "not-allowed" : "pointer",
                    color: !hasMore ? "text.disabled" : "primary.main",
                    pointerEvents: !hasMore ? "none" : "auto",
                    opacity: !hasMore ? 0.6 : 1,
                }}
                onClick={loadMorePayouts}
            >
                {loading ? "loading..." : "Load more"}
            </Typography>
        
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Typography variant="body2" color="text.secondary">
                    {((currentPage - 1) * itemsPerPage) + 1} – {Math.min(currentPage * itemsPerPage, payouts.length)} of {payouts.length}
                </Typography>

                <IconButton 
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                >
                    <ChevronLeft />
                </IconButton>

                <IconButton 
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                >
                    <ChevronRight />
                </IconButton>

                <Button 
                    variant="text" 
                    size="small" 
                    onClick={handleDownloadCSV} 
                    startIcon={<Download />}
                >
                    Export
                </Button>
                </Box>

            </Box>
        </Paper>
    );
}

export default PayoutsTable;